// Shared state for the chat sidecar dock (open/collapsed + width,
// both persisted), split from AnalystDock so leaf components - e.g.
// the artifact cards inside chat messages - can open the dock without
// importing the dock component itself (which imports them back).

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';

const OPEN_KEY = 'predictai.analyst.dock.open';
const WIDTH_KEY = 'predictai.analyst.dock.width';

export const MIN_W = 340;
export const MAX_W = 640;
export const DEFAULT_W = 420;

const clampWidth = (w) => Math.min(MAX_W, Math.max(MIN_W, Math.round(w || DEFAULT_W)));

const DockContext = createContext({
  open: false, width: DEFAULT_W, pushPx: 0,
  openDock: () => {}, collapseDock: () => {}, setWidth: () => {},
  dockActive: false, isMobile: false,
});

export const useAnalystDock = () => useContext(DockContext);

export const AnalystDockProvider = ({ children }) => {
  const location = useLocation();
  const [open, setOpen] = useState(() => {
    // Phones never boot with the sheet covering a page - the pill is
    // the resting state. Desktop honours the persisted preference.
    try {
      if (typeof window !== 'undefined'
          && window.matchMedia('(max-width:899.95px)').matches) {
        return false;
      }
      return localStorage.getItem(OPEN_KEY) === '1';
    } catch { return false; }
  });
  const [width, setWidthState] = useState(() => {
    try { return clampWidth(Number(localStorage.getItem(WIDTH_KEY)) || DEFAULT_W); }
    catch { return DEFAULT_W; }
  });
  // Wide viewports push the page content aside; narrow ones overlay.
  const wide = useMediaQuery('(min-width:1440px)');
  // On phones the docked chat is a full-screen sheet, so it must never
  // sit on top of a page the user just navigated to.
  const isMobile = useMediaQuery('(max-width:899.95px)');
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;

  const openDock = useCallback(() => {
    setOpen(true);
    // A phone open is per-visit; it must not dock the next desktop session.
    if (isMobileRef.current) return;
    try { localStorage.setItem(OPEN_KEY, '1'); } catch { /* private mode */ }
  }, []);
  const collapseDock = useCallback(() => {
    setOpen(false);
    if (isMobileRef.current) return;
    try { localStorage.setItem(OPEN_KEY, '0'); } catch { /* private mode */ }
  }, []);
  const setWidth = useCallback((w) => {
    const next = clampWidth(w);
    setWidthState(next);
    try { localStorage.setItem(WIDTH_KEY, String(next)); } catch { /* private mode */ }
  }, []);

  // Phones: any navigation means the user wants the PAGE - the sheet
  // steps aside into the pill (one tap brings it back). Desktop keeps
  // the sidecar docked across navigation.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;
    if (isMobile) setOpen(false);
  }, [location.pathname, isMobile]);

  // The dock only exists on pages that aren't already a chat surface:
  // /analyst owns the full panel, and a goal's Analyst section renders
  // its own goal-scoped chat.
  const chatOwned = location.pathname === '/analyst'
    || /^\/goals\/[^/]+\/analyst\/?$/.test(location.pathname);
  const dockActive = !chatOwned;
  const pushPx = dockActive && open && wide ? width : 0;

  const value = useMemo(
    () => ({ open, width, pushPx, openDock, collapseDock, setWidth, dockActive, isMobile }),
    [open, width, pushPx, openDock, collapseDock, setWidth, dockActive, isMobile],
  );
  return <DockContext.Provider value={value}>{children}</DockContext.Provider>;
};

// The shell's scrolling content area - same .content-body as before,
// plus the dynamic right padding that makes room for the open dock.
export const ContentBody = ({ children }) => {
  const { pushPx } = useAnalystDock();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:899.95px)');
  // .content-body sits 64px below the top (top: 64px) but keeps
  // min-height: 100vh, so every page carries 64px of dead scroll. Data
  // pages scroll anyway and never notice; the full-page chat must fill
  // the viewport EXACTLY - the thread scrolls internally, the page
  // itself must not (.content-body--fullbleed, dvh-aware for mobile
  // URL bars).
  const fullBleed = location.pathname === '/analyst';
  return (
    <div
      className={`content-body${fullBleed ? ' content-body--fullbleed' : ''}`}
      style={{
        // Mirrors the mobile --sidebar-content-gutter (14px) so both
        // gutters match on phones; desktop keeps 30px.
        paddingRight: (isMobile ? 14 : 30) + pushPx,
        // Matches the dock's slide (same duration + curve) so the panel
        // and the content it pushes move as one gesture.
        transition: 'padding-left 320ms cubic-bezier(0.32, 0.72, 0.24, 1), padding-right 320ms cubic-bezier(0.32, 0.72, 0.24, 1)',
      }}
    >
      {children}
    </div>
  );
};
